import type React from 'react';
import {BOX_PAGE_QUESTION_DETAIL} from '../../../core/entities/box/constants';
import type {
  AdviserResponse,
  QuestionWithAnswerType,
} from '../../../core/repositories/boxRepositoryTypes';
import {CommonLink} from '../../../shared/components/CommonLink';
import {convertNlToBr} from '../../../shared/services/convertNlToBr';
import {formatDate} from '../../../shared/services/formatDate';
import {createBoxPath} from '../usecases/routes';
import {useAnswerLiked} from '../usecases/useAnswerLiked';
import {LikeButton} from './LikeButton';
import {ShareButton} from './ShareButton';

type QuestionProps = {
  question: QuestionWithAnswerType;
  adviser: AdviserResponse;
};

export const AdviserQuestionItem: React.FC<QuestionProps> = ({
  question,
  adviser,
}) => {
  const answer = question.answers[0]!;
  const QuestionDetailURL = createBoxPath({
    page: BOX_PAGE_QUESTION_DETAIL,
    questionId: question.id,
    focusAnswerId: answer.id,
  });

  const {isLiked, likedCount, handleLiked} = useAnswerLiked({
    questionId: question.id,
    answerId: answer.id,
    adviserName: adviser.userName,
    isLikedByAPI: answer.liked,
    likedCountByAPI: answer.likeCount,
  });
  const formattedAnsweredAt = formatDate(answer.answeredAt);

  return (
    <div className="bg-white shadow-card">
      <div className="px-[14px] pb-[14px] pt-16">
        <h2 className="text font-bold leading text-black-1">
          <CommonLink
            href={QuestionDetailURL}
            className={
              'block pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
            }
          >
            {convertNlToBr(question.body)}
          </CommonLink>
        </h2>
        <div className="mt-12 bg-background-1 px-[14px] py-12">
          <p className="line-clamp-2 overflow-hidden text-xs leading text-black-1">
            <CommonLink
              href={QuestionDetailURL}
              className={
                'block break-words pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
              }
            >
              {answer.body}
            </CommonLink>
          </p>
        </div>

        <div className="mt-[14px] flex justify-between">
          {formattedAnsweredAt && (
            <div className="flex items-center justify-end text-sm leading-sm text-black-4">
              <p>回答日：{formattedAnsweredAt}</p>
            </div>
          )}
          <div className="flex items-center">
            <LikeButton
              isLiked={isLiked}
              likedCount={likedCount}
              handleLiked={async () => handleLiked()}
            />
            <ShareButton className="ml-[22px]" path={QuestionDetailURL} />
          </div>
        </div>
      </div>

      {question.answerCount > 1 && (
        <div className="flex justify-center border-t-[1px] border-black-4 py-16">
          <CommonLink
            href={createBoxPath({
              page: BOX_PAGE_QUESTION_DETAIL,
              questionId: question.id,
            })}
            className={
              'text leading text-link-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
            }
          >
            全ての回答を見る
            <span className="pl-4 text-icon-pc">
              (全{question.answerCount}件)
            </span>
          </CommonLink>
        </div>
      )}
    </div>
  );
};
